import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./AdminRetention.css";
import "./Stats.css";

const serverConfig = { withCredentials: true };

// ── Shared sub-components ──────────────────────────────────────────────────

const Stat = ({ label, value, sub }) => (
  <div className="as-card">
    <div className="as-card-value">{value ?? "—"}</div>
    <div className="as-card-label">{label}</div>
    {sub && <div className="as-card-sub">{sub}</div>}
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="as-section-label">{children}</div>
);

// ── Page ─────────────────────────────────────────────────────────────────

const AdminRetention = () => {
  const [retention, setRetention] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [aiBreakdown, setAiBreakdown] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [atRisk, setAtRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const base = config.apiUrl;
    Promise.all([
      axios.get(`${base}/api/internal/retention`,         serverConfig),
      axios.get(`${base}/api/internal/activity-timeline`, serverConfig),
      axios.get(`${base}/api/internal/funnel`,            serverConfig),
      axios.get(`${base}/api/internal/ai-breakdown`,      serverConfig),
      axios.get(`${base}/api/internal/feedback-summary`,  serverConfig),
      axios.get(`${base}/api/internal/at-risk-users`,     serverConfig),
    ])
      .then(([r, t, f, ai, fb, risk]) => {
        setRetention(r.data.metrics);
        setTimeline(t.data.days);
        setFunnel(f.data.steps);
        setAiBreakdown(ai.data.breakdown);
        setFeedback(fb.data);
        setAtRisk(risk.data.users);
      })
      .catch(() => setError("Failed to load analytics data."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="ar-loading">Loading analytics...</div>;
  if (error)   return <div className="ar-error">{error}</div>;

  const m = retention;
  const totalAi = aiBreakdown?.reduce((s, x) => s + x.count, 0) || 0;

  return (
    <div className="as-page">
      {/* Header */}
      <div className="as-header">
        <div className="as-eyebrow">Admin</div>
        <h1 className="as-title">Retention & Engagement</h1>
        <p className="as-subtitle">User behaviour, activation funnel, and satisfaction data.</p>
      </div>

      {/* ── 1. Health Overview ── */}
      <section className="as-section">
        <SectionLabel>Health Overview</SectionLabel>
        <div className="as-grid as-grid-4">
          <Stat label="Total Users"  value={m?.totalUsers} />
          <Stat label="WAU"          value={m?.wau}  sub="active last 7 days" />
          <Stat label="MAU"          value={m?.mau}  sub="active last 30 days" />
          <Stat label="Stickiness"   value={m?.stickiness} sub="WAU / MAU" />
        </div>
      </section>

      {/* ── 2. Retention + Activation ── */}
      <section className="as-section">
        <SectionLabel>Retention + Activation</SectionLabel>
        <div className="as-grid as-grid-4" style={{ gridTemplateColumns: "repeat(5,1fr)" }}>
          <Stat label="W1 Retention"     value={m?.cohortRetention?.W1 ?? "—"} sub="returned after week 1" />
          <Stat label="W4 Retention"     value={m?.cohortRetention?.W4 ?? "—"} sub="returned after week 4" />
          <Stat label="W8 Retention"     value={m?.cohortRetention?.W8 ?? "—"} sub="returned after week 8" />
          <Stat label="Activation Rate"  value={m?.activationRate} sub="users with ≥1 update" />
          <Stat label="AI Adoption"      value={m?.aiAdoptionRate} sub="users who used AI" />
        </div>
      </section>

      {/* ── 3. Activity Timeline ── */}
      {timeline?.length > 0 && (
        <section className="as-section">
          <SectionLabel>Activity — last 30 days</SectionLabel>
          <div className="ar-chart-wrap">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={timeline} barSize={6} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#ccc", fontSize: 9 }}
                  tickFormatter={(v) => v.slice(5)}
                  interval={4}
                />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: "#ccc", fontSize: 9 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #efefef", borderRadius: 8, fontSize: "0.78rem" }}
                  cursor={{ fill: "rgba(0,0,0,0.04)" }}
                />
                <Legend iconType="circle" iconSize={7} wrapperStyle={{ fontSize: "0.72rem", paddingTop: 8 }} />
                <Bar dataKey="session_start"   name="Sessions"       fill="#6366f1" radius={[2,2,0,0]} />
                <Bar dataKey="node_updated"    name="Updates"        fill="#22c55e" radius={[2,2,0,0]} />
                <Bar dataKey="ai_assist_used"  name="AI Assist"      fill="#f17501" radius={[2,2,0,0]} />
                <Bar dataKey="plan_created"    name="Plans created"  fill="#06b6d4" radius={[2,2,0,0]} />
                <Bar dataKey="report_generated" name="Reports"       fill="#a855f7" radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* ── 4. Activation Funnel ── */}
      {funnel?.length > 0 && (
        <section className="as-section">
          <SectionLabel>Activation Funnel</SectionLabel>
          <div className="ar-funnel">
            {funnel.map((step, i) => {
              const prev = i > 0 ? funnel[i - 1].count : null;
              const dropPct = prev && prev > 0
                ? (((prev - step.count) / prev) * 100).toFixed(0)
                : null;
              return (
                <div key={step.label} className="ar-funnel-step">
                  {i > 0 && (
                    <div className="ar-funnel-arrow">
                      {dropPct !== null && <span className="ar-funnel-drop">-{dropPct}%</span>}
                      <span className="ar-funnel-chevron">›</span>
                    </div>
                  )}
                  <div className="ar-funnel-box">
                    <div className="ar-funnel-count">{step.count.toLocaleString()}</div>
                    <div className="ar-funnel-label">{step.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── 5. AI Breakdown ── */}
      {aiBreakdown?.length > 0 && (
        <section className="as-section">
          <SectionLabel>AI Usage Breakdown</SectionLabel>
          <div className="as-grid as-grid-4" style={{ gridTemplateColumns: `repeat(${Math.min(aiBreakdown.length, 4)}, 1fr)` }}>
            {aiBreakdown.map((row) => (
              <div key={row.assistType} className="as-card ar-ai-card">
                <div className="as-card-value">{row.count}</div>
                <div className="as-card-label">{row.assistType}</div>
                <div className="ar-ai-bar-wrap">
                  <div className="ar-ai-bar" style={{ width: totalAi > 0 ? `${(row.count / totalAi) * 100}%` : "0%" }} />
                </div>
                <div className="as-card-sub">{totalAi > 0 ? ((row.count / totalAi) * 100).toFixed(1) : "0"}% of uses</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── 6. Feedback Summary ── */}
      {feedback && (
        <section className="as-section">
          <SectionLabel>Feedback Summary</SectionLabel>
          {feedback.totalResponses === 0 ? (
            <p className="ar-empty">No feedback collected yet.</p>
          ) : (
            <div className="ar-feedback-grid">
              {/* Left: avg + distribution */}
              <div className="ar-feedback-left">
                <div className="ar-rating-big">
                  <span className="ar-rating-value">{feedback.avgRating}</span>
                  <span className="ar-rating-star">★</span>
                  <span className="ar-rating-total">{feedback.totalResponses} responses</span>
                </div>
                <div className="ar-dist">
                  {[5, 4, 3, 2, 1].map((n) => {
                    const count = feedback.distribution[n] || 0;
                    const pct = feedback.totalResponses > 0 ? (count / feedback.totalResponses) * 100 : 0;
                    return (
                      <div key={n} className="ar-dist-row">
                        <span className="ar-dist-label">{n}★</span>
                        <div className="ar-dist-bar-wrap">
                          <div className="ar-dist-bar" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="ar-dist-count">{count}</span>
                      </div>
                    );
                  })}
                </div>
                {feedback.byTrigger?.length > 0 && (
                  <div className="ar-trigger-table">
                    <div className="ar-trigger-head">
                      <span>Trigger</span><span>Count</span><span>Avg</span>
                    </div>
                    {feedback.byTrigger.map((t) => (
                      <div key={t.trigger} className="ar-trigger-row">
                        <span>{t.trigger}</span>
                        <span>{t.count}</span>
                        <span>{t.avgRating}★</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: recent comments */}
              {feedback.recentComments?.length > 0 && (
                <div className="ar-feedback-right">
                  <div className="ar-comments-label">Recent comments</div>
                  <div className="ar-comments-list">
                    {feedback.recentComments.map((c, i) => (
                      <div key={i} className="ar-comment">
                        <span className="ar-comment-stars">{"★".repeat(c.rating)}{"☆".repeat(5 - c.rating)}</span>
                        <p className="ar-comment-text">{c.comment}</p>
                        <span className="ar-comment-trigger">{c.trigger}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ── 7. At-Risk Users ── */}
      <section className="as-section">
        <SectionLabel>At-Risk Users — created plan, never updated</SectionLabel>
        {!atRisk || atRisk.length === 0 ? (
          <p className="ar-empty">No at-risk users right now.</p>
        ) : (
          <div className="ar-table-wrap">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Registered</th>
                  <th>Country</th>
                </tr>
              </thead>
              <tbody>
                {atRisk.map((u) => (
                  <tr key={u._id}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                    <td>{u.country || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminRetention;
